import React, { useState, useEffect } from 'react';
import { Modal, Button, Space, Spin, message } from 'antd';
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  CloseOutlined
} from '@ant-design/icons';
import api from '../utils/axios';

// 同源 /api/ 资源可能需要登录凭证，裸 <img> 不带 token 会 401，需用 axios 拉 blob
const needsAuthFetch = (url) => typeof url === 'string' && /^\/api\//.test(url);

// axios 实例的 baseURL 已是 /api，调用方传入的 /api/ 开头完整路径必须剥掉前缀，
// 否则实际请求会变成 /api/api/...，命中 SPA 兜底路由返回 200 + HTML（假成功），图片解码失败
const stripApiPrefix = (url) => (typeof url === 'string' ? url.replace(/^\/api(?=\/)/, '') : url);

// SPA 兜底/网关异常可能以 200 返回 HTML/JSON，不能当作图片数据交给 <img>
const isFakeImageResponse = (blob) =>
  !!blob && !!blob.type && /text\/html|application\/json/i.test(blob.type);

// 部分下载接口返回 application/octet-stream，按文件名扩展名补正确的图片 MIME，保证 <img> 能渲染
const MIME_BY_EXT = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml'
};
const guessImageMime = (name) => {
  const ext = String(name || '').split('?')[0].split('.').pop().toLowerCase();
  return MIME_BY_EXT[ext] || '';
};
const normalizeImageBlob = (blob, name) => {
  if (blob && blob.type && blob.type.startsWith('image/')) return blob;
  const mime = guessImageMime(name);
  return mime ? new Blob([blob], { type: mime }) : blob;
};

/**
 * 图片查看器组件
 * 支持旋转、缩放、全屏、下载等功能
 */
const ImageViewer = ({ 
  visible, 
  onClose, 
  imageUrl, 
  imageName = '图片',
  downloadUrl = null // 如果提供，使用此URL下载，否则使用imageUrl
}) => {
  const [loading, setLoading] = useState(false);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [modalWidth, setModalWidth] = useState('90vw');
  // 实际用于 <img> 的地址：鉴权接口会先换成 blob objectURL
  const [resolvedUrl, setResolvedUrl] = useState('');

  // 重置状态
  const resetTransform = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  // 当Modal打开时重置状态，并把图片地址解析为可直接展示的 URL
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    if (!visible) {
      setResolvedUrl('');
      return undefined;
    }

    resetTransform();
    setLoading(true);

    // 超时兜底，防止 loading 不消失
    const timeoutId = setTimeout(() => setLoading(false), 10000);

    if (!imageUrl) {
      clearTimeout(timeoutId);
      setLoading(false);
      setResolvedUrl('');
      return undefined;
    }

    if (needsAuthFetch(imageUrl)) {
      // 同源鉴权接口：带 token 拉成 blob，再交给 <img>
      api.get(stripApiPrefix(imageUrl), { responseType: 'blob' })
        .then((resp) => {
          if (cancelled) return;
          // 兜底路由返回的 HTML/JSON 不能当图片，回退直连交给 <img> 的 onError 提示
          if (isFakeImageResponse(resp.data)) {
            setResolvedUrl(imageUrl);
            return;
          }
          objectUrl = URL.createObjectURL(normalizeImageBlob(resp.data, imageName));
          setResolvedUrl(objectUrl);
        })
        .catch(() => {
          // 拉取失败（如 302 到 OSS 被跨域拦截等）回退直连，交给 <img> 的 onError 提示
          if (!cancelled) setResolvedUrl(imageUrl);
        });
    } else {
      setResolvedUrl(imageUrl);
    }

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, imageUrl]);

  // 监听窗口大小变化，重新计算Modal宽度
  useEffect(() => {
    const handleResize = () => {
      if (visible && imageSize.width > 0) {
        updateModalWidth(imageSize.width, imageSize.height);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [visible, imageSize]);

  // 图片加载完成
  const handleImageLoad = (e) => {
    setLoading(false);
    const img = e.target;
    const { naturalWidth, naturalHeight } = img;
    setImageSize({ width: naturalWidth, height: naturalHeight });
    
    // 根据图片尺寸调整Modal宽度
    updateModalWidth(naturalWidth, naturalHeight);
  };

  // 更新Modal宽度的函数
  const updateModalWidth = (width, height) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // 计算适合的显示尺寸
    const maxWidth = viewportWidth * 0.9;
    const maxHeight = viewportHeight * 0.8;
    
    let displayWidth = width;
    let displayHeight = height;
    
    // 如果图片太大，按比例缩放
    if (width > maxWidth || height > maxHeight) {
      const scaleX = maxWidth / width;
      const scaleY = maxHeight / height;
      const scale = Math.min(scaleX, scaleY);
      displayWidth = width * scale;
      displayHeight = height * scale;
    }
    
    // 设置Modal宽度，为图片宽度留出一些边距
    const modalWidthValue = Math.min(displayWidth + 100, viewportWidth * 0.95);
    setModalWidth(`${modalWidthValue}px`);
  };

  // 图片加载失败
  const handleImageError = () => {
    setLoading(false);
    message.error('图片加载失败');
  };

  // 放大
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 5));
  };

  // 缩小
  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.1));
  };

  // 左旋转
  const handleRotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  // 右旋转
  const handleRotateRight = () => {
    setRotation(prev => prev + 90);
  };

  // 下载图片
  const handleDownload = async () => {
    const targetUrl = downloadUrl || imageUrl;
    try {
      let href = targetUrl;
      let objectUrl = null;
      if (needsAuthFetch(targetUrl)) {
        // 鉴权接口需带 token 拉取，否则 a 标签直接访问会 401；
        // 拉取失败（如 302 到 OSS 被跨域拦截）时降级为直接导航，浏览器会跟随重定向打开/下载
        try {
          const resp = await api.get(stripApiPrefix(targetUrl), { responseType: 'blob' });
          if (!isFakeImageResponse(resp.data)) {
            objectUrl = URL.createObjectURL(resp.data);
            href = objectUrl;
          }
        } catch (e) {
          /* 降级为直接导航 */
        }
      }
      const link = document.createElement('a');
      link.href = href;
      link.download = imageName || '图片';
      link.target = '_blank';
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      message.success('开始下载');
    } catch (error) {
      message.error('下载失败');
    }
  };

  // 全屏切换
  const handleFullscreen = () => {
    const modalElement = document.querySelector('.image-viewer-modal .ant-modal-content');
    if (modalElement) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        modalElement.requestFullscreen();
      }
    }
  };

  // 鼠标按下开始拖拽
  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // 鼠标移动拖拽
  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  // 鼠标抬起结束拖拽
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 滚轮缩放
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.min(Math.max(prev * delta, 0.1), 5));
  };

  // 双击重置
  const handleDoubleClick = () => {
    if (scale === 1 && rotation === 0 && position.x === 0 && position.y === 0) {
      // 如果是初始状态，放大到2倍
      setScale(2);
    } else {
      // 否则重置
      resetTransform();
    }
  };

  const imageStyle = {
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
    transition: isDragging ? 'none' : 'transform 0.3s ease',
    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
    display: 'block'
  };

  const modalBodyStyle = {
    padding: 0,
    textAlign: 'center',
    backgroundColor: '#000',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative'
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={modalWidth}
      centered
      className="image-viewer-modal"
      styles={{
        body: modalBodyStyle
      }}
      closeIcon={
        <CloseOutlined 
          style={{ 
            color: '#fff', 
            fontSize: '18px',
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: '8px',
            borderRadius: '4px'
          }} 
        />
      }
    >
      {/* 工具栏 */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1001,
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: '8px',
        padding: '8px'
      }}>
        <Space>
          <Button 
            type="text" 
            icon={<ZoomOutOutlined />} 
            onClick={handleZoomOut}
            style={{ color: '#fff' }}
            title="缩小"
          />
          <span style={{ color: '#fff', minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <Button 
            type="text" 
            icon={<ZoomInOutlined />} 
            onClick={handleZoomIn}
            style={{ color: '#fff' }}
            title="放大"
          />
          <Button 
            type="text" 
            icon={<RotateLeftOutlined />} 
            onClick={handleRotateLeft}
            style={{ color: '#fff' }}
            title="左旋转"
          />
          <Button 
            type="text" 
            icon={<RotateRightOutlined />} 
            onClick={handleRotateRight}
            style={{ color: '#fff' }}
            title="右旋转"
          />
          <Button 
            type="text" 
            icon={<FullscreenOutlined />} 
            onClick={handleFullscreen}
            style={{ color: '#fff' }}
            title="全屏"
          />
          <Button 
            type="text" 
            icon={<DownloadOutlined />} 
            onClick={handleDownload}
            style={{ color: '#fff' }}
            title="下载"
          />
        </Space>
      </div>

      {/* 图片容器 */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
      >
        {loading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1002
          }}>
            <Spin size="large" style={{ color: '#fff' }} />
          </div>
        )}
        
        {resolvedUrl && (
          <img
            src={resolvedUrl}
            alt={imageName}
            style={imageStyle}
            onLoad={handleImageLoad}
            onError={handleImageError}
            draggable={false}
          />
        )}
      </div>

      {/* 操作提示 */}
      <div style={{
        position: 'absolute',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        color: '#fff',
        fontSize: '12px',
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: '4px 12px',
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        滚轮缩放 | 拖拽移动 | 双击重置
      </div>
    </Modal>
  );
};

export default ImageViewer;
